import React, { ReactElement, useMemo, useRef, Dispatch, SetStateAction } from 'react'
import { Space, Row, Col, Button, Checkbox, Select } from 'antd'
import { LineBreak } from '../../../atom/line-break'
import { ButtonIcon } from '../../../atom/button'
import { magnifyBlackSVG } from '../../../assets/svg/magnify'
import { ReusableSelector } from '../../../components/reusableSelector/selector'
import { useAppState } from '../../../state'
import { useGeneralTranslation } from '../../../hooks/translation/useGeneralTranslation'
import { Integration } from '../types'
import { debounce } from 'lodash'

interface IntegrationsSelectorsProps {
  loading: boolean
  onIntegrationChange: (values: string[]) => void
  selectedIntegrations: string[]
  selectedCategories: string[]
  onCategoryChange: (values: string[]) => void
  selectedPropertyTypes: string[]
  onPropertyTypeChange: (values: string[]) => void
  categoryOptions: string[]
  propertyTypeOptions: string[]
  onSearch: () => void
  dropdownClassName?: string
  integrations: Integration[]
}

const IntegrationsSelectors: React.FC<IntegrationsSelectorsProps> = ({
  loading,
  onIntegrationChange,
  selectedIntegrations,
  selectedCategories,
  onCategoryChange,
  selectedPropertyTypes,
  onPropertyTypeChange,
  categoryOptions,
  propertyTypeOptions,
  onSearch,
  dropdownClassName,
  integrations,
}) => {
  const { IsDesktop, IsLaptop, isMockUpEnabled } = useAppState()
  const { searchText: searchTextTranslation } = useGeneralTranslation()
  const isDesktop = IsDesktop()
  const isLaptop = IsLaptop()

  const renderCategoryOption = (
    option: string,
    selectedOptions: string[],
    handleCheckboxClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  ): ReactElement => (
    <Select.Option key={option} value={option} label={option}>
      <Checkbox onClick={handleCheckboxClick} checked={selectedOptions.includes(option)}>
        {option}
      </Checkbox>
    </Select.Option>
  )

  const renderPropertyTypeOption = (
    option: string,
    selectedOptions: string[],
    handleCheckboxClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  ): ReactElement => (
    <Select.Option key={option} value={option} label={option}>
      <Checkbox onClick={handleCheckboxClick} checked={selectedOptions.includes(option)}>
        {option}
      </Checkbox>
    </Select.Option>
  )

  const handleCategoryOptionChange = (
    value: any | any[],
    setDirty: (dirty: boolean) => void,
    setSelectedOptions: (selectedOption: any[]) => void,
    onOptionsChange: (selectedOptions: any[]) => void,
  ) => {
    setDirty(true)
    const selected = Array.isArray(value) ? value : [value]
    setSelectedOptions(selected)
    onOptionsChange(selected)
  }

  const handlePropertyTypeOptionChange = (
    value: any | any[],
    setDirty: (dirty: boolean) => void,
    setSelectedOptions: (selectedOption: any[]) => void,
    onOptionsChange: (selectedOptions: any[]) => void,
  ) => {
    setDirty(true)
    const selected = Array.isArray(value) ? value : [value]
    setSelectedOptions(selected)
    onOptionsChange(selected)
  }

  const fetchRef = useRef(0)

  const integrationDebounceFetcher = useMemo(() => {
    const loadOptions = (
      value: string,
      setValues: Dispatch<SetStateAction<any[]>>,
      setFetching: (fetching: boolean) => void,
      isMockUpEnabled: boolean,
      setValueOptions: Dispatch<SetStateAction<any[]>>,
    ) => {
      if (value && value.trim().length > 0) {
        fetchRef.current += 1
        const fetchId = fetchRef.current
        setValues([])
        setFetching(true)

        const searchLower = value.toLowerCase()
        const filtered = integrations
          .filter((integration) => 
            integration.name.toLowerCase().startsWith(searchLower) && 
            !selectedIntegrations.includes(integration.name)
          )
          .map((integration) => integration.name)

        setTimeout(() => {
          if (fetchId !== fetchRef.current) {
            return
          }
          setValues(filtered)
          setValueOptions((prev) => [...prev, ...filtered])
          setFetching(false)
        }, 100)
      } else {
        setValues([])
        setFetching(false)
      }
    }

    return debounce(loadOptions, 300)
  }, [integrations, selectedIntegrations])

  const multiIntegrationPlaceholder = (selectedOptions: string[]) => {
    const selected = selectedOptions.length
    return selected === 0 ? 'Start typing to search' : selected === 1 ? selectedOptions[0] : `${selected} Integrations Selected`
  }

  const handleIntegrationOptionChange = (
    value: any | any[],
    setDirty: (dirty: boolean) => void,
    setSelectedOptions: (selectedOption: any[]) => void,
    onOptionsChange: (selectedOptions: any[]) => void,
  ) => {
    setDirty(true)
    const selected = Array.isArray(value) ? value : [value]
    setSelectedOptions(selected)
    onOptionsChange(selected)
  }

  const renderIntegrationOption = (
    option: string,
    selectedOptions: string[],
    handleCheckboxClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void,
  ): ReactElement => (
    <Select.Option key={option} value={option} label={option}>
      <Checkbox onClick={handleCheckboxClick} checked={selectedOptions.includes(option)}>
        {option}
      </Checkbox>
    </Select.Option>
  )

  const renderSearchInput = () => (
    <div className="integrations-name-selector">
      <ReusableSelector
        showSearch
        onOptionsChange={onIntegrationChange}
        onClear={() => onIntegrationChange([])}
        combineType="right"
        customWidth="210px"
        isSingle={false}
        placeholder="Start typing to search"
        dropDownList={[]}
        showMagnifySVG={true}
        maxTagCount={0}
        nativeSearch={false}
        isDebounceFetcher={true}
        debounceFetcher={integrationDebounceFetcher}
        handlePlaceholder={multiIntegrationPlaceholder}
        handleOptionChange={handleIntegrationOptionChange}
        renderOption={renderIntegrationOption}
        isMockUpEnabled={isMockUpEnabled}
        dropdownClassName={dropdownClassName}
        defaultValues={selectedIntegrations}
      />
    </div>
  )

  const multiCategoryPlaceholder = (selectedOptions: string[]) => {
    const selected = selectedOptions.length
    return selected === 0 ? 'Category' : selected === 1 ? selectedOptions[0] : `${selected} Categories Selected`
  }

  const multiPropertyTypePlaceholder = (selectedOptions: string[]) => {
    const selected = selectedOptions.length
    return selected === 0 ? 'Property Type' : selected === 1 ? selectedOptions[0] : `${selected} Property Types Selected`
  }

  const renderCategorySelector = () => (
    <div className="integrations-category-selector">
      <ReusableSelector
        showSearch
        onOptionsChange={(opts: string[]) => onCategoryChange(opts)}
        onClear={() => onCategoryChange([])}
        combineType="both"
        customWidth="210px"
        isSingle={false}
        placeholder="Category"
        dropDownList={categoryOptions}
        showMagnifySVG={false}
        maxTagCount={0}
        nativeSearch
        isDebounceFetcher={false}
        debounceFetcher={undefined}
        handlePlaceholder={multiCategoryPlaceholder}
        handleOptionChange={handleCategoryOptionChange}
        renderOption={renderCategoryOption}
        isMockUpEnabled={isMockUpEnabled}
        dropdownClassName={dropdownClassName}
      />
    </div>
  )

  const renderPropertyTypeSelector = () => (
    <div className="integrations-property-type-selector">
      <ReusableSelector
        showSearch
        onOptionsChange={(opts: string[]) => onPropertyTypeChange(opts)}
        onClear={() => onPropertyTypeChange([])}
        combineType="left"
        customWidth="230px"
        isSingle={false}
        placeholder="Property Type"
        dropDownList={propertyTypeOptions}
        showMagnifySVG={false}
        maxTagCount={0}
        nativeSearch
        isDebounceFetcher={false}
        debounceFetcher={undefined}
        handlePlaceholder={multiPropertyTypePlaceholder}
        handleOptionChange={handlePropertyTypeOptionChange}
        renderOption={renderPropertyTypeOption}
        isMockUpEnabled={isMockUpEnabled}
        dropdownClassName={dropdownClassName}
      />
    </div>
  )

  return (
    <>
      {isDesktop || isLaptop ? (
        <Space wrap size={[0, 6]} style={{ display: 'flex' }}>
          {renderSearchInput()}
          <LineBreak />
          {renderCategorySelector()}
          <LineBreak />
          {renderPropertyTypeSelector()}
          <ButtonIcon
            icon={magnifyBlackSVG}
            loading={loading}
            disabled={loading}
            fetchData={onSearch}
            withinSearchBar
          />
        </Space>
      ) : (
        <Row gutter={[8, 8]}>
          <Col xs={24} md={12}>
            {renderSearchInput()}
          </Col>
          <Col xs={24} md={12}>
            {renderCategorySelector()}
          </Col>
          <Col xs={24} md={12}>
            {renderPropertyTypeSelector()}
          </Col>
          <Col xs={24} md={12}>
            <Button block type="primary" disabled={loading} loading={loading} onClick={onSearch} style={{ margin: '0 5px' }}>
              {searchTextTranslation}
            </Button>
          </Col>
        </Row>
      )}
    </>
  )
}

export default IntegrationsSelectors

